

import * as ZapparThree from '@zappar/zappar-threejs';
import { DataTexture, Group, PerspectiveCamera, Scene } from "three";
import type Renderer from "./Renderer";
import { Params } from '../utils/types';
import { ImageTracker } from "./ImageTracker";
import Pivot from "./Pivot";
import Intro from './components/intro';
import Lights from './components/lights';
import Main from './components/main';
import { LoadedGLTFs, LoadedTextures } from './utils/types';
import { hideIntroFooter, revealPrompt } from '../dom';

export default class World {
    declare imageTracker: ImageTracker;
    declare intro: Intro;
    declare main: Main;
    private scene: Scene;
    private zapparCamera: ZapparThree.Camera;
    private perspectiveCamera: PerspectiveCamera;
    private gltfs: LoadedGLTFs;
    private textures: LoadedTextures;
    private envMap: DataTexture;
    private urlParams: Params;
    private declare renderer: Renderer;
    public declare pivot: Pivot;
    declare onScreen: Group;
    private introActive: boolean;
    private lights: Lights;
    private phoneDown: boolean

    constructor(
        scene: Scene,
        zapparCamera: ZapparThree.Camera,
        camera: PerspectiveCamera,
        gltfs: LoadedGLTFs,
        textures: LoadedTextures,
        envMap: DataTexture,
        urlParams: Params,
        renderer: Renderer,

    ) {
        this.scene = scene;
        this.zapparCamera = zapparCamera;
        this.perspectiveCamera = camera;
        this.renderer = renderer;
        this.gltfs = gltfs;
        this.textures = textures,
            this.envMap = envMap;
        this.urlParams = urlParams;

        this.lights = new Lights()
        this.introActive = true;
        this.phoneDown = false;

        this.setUpPivot();
        this.setUpOnScreen();
        this.setImageTracker();

        this.setUpIntro();
        this.setUpMain()

        this.switchToGyro();
        this.scene.background = this.zapparCamera.backgroundTexture;

        this.init()
    }

    private setUpPivot() {
        this.pivot = new Pivot(this.perspectiveCamera);
        this.scene.add(this.pivot.instance);
        this.pivot.rotatePivotToCamera()
    }


    private setUpOnScreen() {
        this.onScreen = new Group();
        this.scene.add(this.onScreen);
    }


    private setUpIntro = async () => {
        this.intro = new Intro(
            this.gltfs,
            this.envMap,
            this.urlParams,
            this.lights)
        this.pivot.rotatePivotToCamera() // temp
        this.intro?.updatePhoneDirection(this.phoneDown)
    }

    private setUpMain() {
        this.main = new Main(
            this.renderer,
            this.perspectiveCamera,
            this.zapparCamera,
            this.scene,
            this.gltfs,
            this.textures,
            this.onScreen,
            this.lights,
            this.urlParams
        );
    }

    public init = async () => {
        if (this.urlParams.gyro) {
            this.start();
            hideIntroFooter()
        }
        else {
            revealPrompt();
            this.intro.start().then(this.start)
        }
    }

    public start = async () => {
        this.pivot.rotatePivotToCamera()
        this.intro.playIntro()
            .then(() => {
                this.introActive = false
                this.renderer.toggleTracking(false)
                this.main.start()
            })
    }

    private setImageTracker() {
        this.imageTracker = new ImageTracker(
            this.zapparCamera,
            this.urlParams
        );
        this.scene.add(this.imageTracker.imageTrackerGroup)
        this.imageTracker.imageTracker.onVisible.bind(this.switchToImageTracker);
        this.imageTracker.imageTracker.onNotVisible.bind(this.switchToGyro);
    }

    public switchToImageTracker = () => {
        if (this.introActive && !this.urlParams.gyro) {
            console.log('switch to image tracker')
            this.intro.instance.position.z = 0;
            this.intro.toggleTracking(true);
            this.imageTracker.imageTrackerGroup.add(this.intro.instance);
            this.renderer.toggleTracking(true)
            this.pivot.rotatePivotToCamera()
            this.start();
        }
    }

    public switchToGyro = () => {
        if (this.introActive) {
            console.log('switch to gyro')
            let posZ = this.urlParams.penNum == 12 ? -2.2 : this.urlParams.penNum == 5 ? - 4 : -6;
            this.intro.instance.position.z = posZ
            this.intro.toggleTracking(false);
            this.pivot.addToPivot(this.intro.instance);
            this.pivot.rotatePivotToCamera()
            this.renderer.toggleTracking(false)
        }
    }

    public update = (deltaTime: number) => {
        this.onScreen.rotation.copy(this.perspectiveCamera.rotation);
        this.intro?.update(deltaTime);
        this.main.update();
    };

    public triggerDrawControl = (trigger: string) => {
        this.main.triggerDrawControl(trigger)
    }

    public updatePhoneDirection = (phoneDown: boolean) => {
        this.phoneDown = phoneDown;
        this.intro?.updatePhoneDirection(phoneDown);
        if (this.introActive) {
            if (!phoneDown) {
                this.pivot.rotatePivotToCamera()
            }
        }
    }
}
